import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { X, Send, Scissors, CreditCard } from "lucide-react";
import { FaWhatsapp } from "react-icons/fa";
import { LINKS, openExternal, waIntent, WHATSAPP_DIGITS } from "@/constants/links";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const QUICK_EXTRA = ["Sposta appuntamento", "Annulla appuntamento"];

const priceFor = (services, servizio) => {
  const s = (servizio || "").trim().toLowerCase();
  for (const svc of services) {
    if (svc.nome.trim().toLowerCase() === s) return svc.prezzo;
  }
  // compatibilità con nomi vecchi
  const taglio = s.includes("taglio") || s.includes("capelli");
  const barba = s.includes("barba");
  if (taglio && barba) return 20;
  if (barba) return 10;
  if (taglio) return 15;
  return null;
};

export default function BookingChat({ onClose }) {
  const [services, setServices] = useState([]);
  const [teamNames, setTeamNames] = useState([]);
  useEffect(() => {
    fetch(`${BACKEND}/api/services`)
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setServices(d))
      .catch(() => {});
    fetch(`${BACKEND}/api/team/public`)
      .then((r) => r.json())
      .then((d) => Array.isArray(d) && setTeamNames(d))
      .catch(() => {});
  }, []);
  const QUICK = [
    ...services.map((s) => ({ label: s.nome, msg: s.nome })),
    ...teamNames.map((t) => ({ label: `Con ${t}`, msg: `Vorrei un appuntamento con ${t}` })),
    ...QUICK_EXTRA.map((q) => ({ label: q, msg: q })),
  ];
  const [sessionId] = useState(() => {
    let id = localStorage.getItem("bl_session_v2");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("bl_session_v2", id);
    }
    return id;
  });
  const [messages, setMessages] = useState([
    {
      role: "assistant",
      text: "Ciao! Sono Jordan, l'assistente del salone. Ti prenoto l'appuntamento in un attimo — come ti chiami?",
    },
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [cancelled, setCancelled] = useState(null);
  const [rescheduled, setRescheduled] = useState(undefined);
  const [waitlist, setWaitlist] = useState(null);
  const [blocked, setBlocked] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading, booking]);

  const send = async (text) => {
    const clean = text.trim();
    if (!clean || loading) return;
    setInput("");
    setLoading(true);
    setMessages((m) => [...m, { role: "user", text: clean }, { role: "assistant", text: "" }]);

    try {
      const res = await fetch(`${BACKEND}/api/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: clean }),
      });
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.trim();
          if (!line.startsWith("data:")) continue;
          const payload = line.slice(5).trim();
          if (payload === "[DONE]") continue;
          try {
            const data = JSON.parse(payload);
            if (data.delta) acc += data.delta;
            if (data.booking) {
              setBooking(data.booking);
              localStorage.setItem("bl_session_v2", crypto.randomUUID());
            }
            if (data.waitlist) {
              setWaitlist(data.waitlist);
              localStorage.setItem("bl_session_v2", crypto.randomUUID());
            }
            if (data.cancelled !== undefined) {
              setCancelled(data.cancelled ? "ok" : "notfound");
              localStorage.setItem("bl_session_v2", crypto.randomUUID());
            }
            if (data.rescheduled !== undefined) {
              setRescheduled(data.rescheduled);
              localStorage.setItem("bl_session_v2", crypto.randomUUID());
            }
            if (data.blocked) {
              setBlocked(true);
              localStorage.setItem("bl_session_v2", crypto.randomUUID());
            }
          } catch {
            // sessione non critica: ignora errori di ripristino
          }
        }
        const visible = acc.split("[[")[0].trim();
        setMessages((m) => {
          const copy = [...m];
          copy[copy.length - 1] = { role: "assistant", text: visible };
          return copy;
        });
      }
    } catch {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          text: "Ops, qualcosa non ha funzionato. Riprova tra un attimo o scrivimi su WhatsApp.",
        };
        return copy;
      });
    }
    setLoading(false);
  };

  const waTextBody = booking
    ? `Nuova prenotazione — ${booking.nome}, ${booking.servizio}, ${booking.giorno} alle ${booking.ora}`
    : "";
  const waText = booking
    ? `https://wa.me/393889378525?text=${encodeURIComponent(waTextBody)}`
    : LINKS.whatsapp;

  return (
    <motion.div
      data-testid="booking-chat-overlay"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 40 }}
      transition={{ duration: 0.25, ease: "easeOut" }}
      onTouchStart={(e) => e.stopPropagation()}
      onTouchEnd={(e) => e.stopPropagation()}
      className="fixed inset-0 z-[60] mx-auto flex w-full max-w-md flex-col bg-zinc-950"
    >
      <header className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
        <span className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400/15 text-amber-400">
          <Scissors size={18} />
        </span>
        <div className="flex-1">
          <p className="text-sm font-extrabold text-white">Jordan — Assistente</p>
          <p className="text-[11px] text-zinc-500">Prenotazioni Barber Latin</p>
        </div>
        <button
          data-testid="chat-close"
          onClick={onClose}
          className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400 transition-colors duration-200 hover:text-white"
        >
          <X size={17} />
        </button>
      </header>

      <div ref={scrollRef} data-testid="chat-messages" className="flex-1 space-y-3 overflow-y-auto px-5 py-4">
        {messages.map((m, i) => (
          <div
            key={i}
            data-testid={`chat-msg-${i}`}
            className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
              m.role === "user"
                ? "ml-auto rounded-br-md bg-amber-400 font-semibold text-zinc-950"
                : "rounded-bl-md border border-white/10 bg-zinc-900 text-zinc-200"
            }`}
          >
            {m.text}
            {m.role === "assistant" && loading && i === messages.length - 1 && !m.text && (
              <span className="inline-block animate-pulse text-zinc-500">…</span>
            )}
          </div>
        ))}

        {cancelled === "ok" && (
          <div
            data-testid="cancel-summary"
            className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-red-400">
              Prenotazione annullata
            </p>
            <p className="mt-1 text-sm text-zinc-300">
              La tua prenotazione è stata annullata correttamente. Il barbiere è stato avvisato.
            </p>
          </div>
        )}
        {cancelled === "notfound" && (
          <div
            data-testid="cancel-notfound"
            className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4"
          >
            <p className="text-sm text-zinc-300">
              Non ho trovato una prenotazione con quei dati. Controlla nome e numero, o scrivici su
              WhatsApp.
            </p>
          </div>
        )}

        {rescheduled && (
          <div
            data-testid="reschedule-summary"
            className="space-y-2 rounded-2xl border border-blue-500/30 bg-blue-500/10 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-blue-400">
              Prenotazione spostata
            </p>
            <p className="text-sm text-zinc-200">
              {rescheduled.nome} — {rescheduled.servizio}, {rescheduled.giorno} alle {rescheduled.ora}
            </p>
            <p className="text-xs leading-relaxed text-zinc-400">
              {rescheduled.status === "attesa"
                ? "Il nuovo slot è occupato: sei in lista d'attesa, ti avvisiamo su WhatsApp appena si libera."
                : "L'agenda del barbiere è già aggiornata con il nuovo orario. Ti aspettiamo!"}
            </p>
          </div>
        )}
        {rescheduled === null && (
          <div
            data-testid="reschedule-notfound"
            className="rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4"
          >
            <p className="text-sm text-zinc-300">
              Non ho trovato una prenotazione con quei dati. Controlla nome e numero, o scrivici su
              WhatsApp.
            </p>
          </div>
        )}

        {blocked && (
          <div
            data-testid="blocked-summary"
            className="rounded-2xl border border-red-500/30 bg-red-500/10 p-4"
          >
            <p className="text-sm text-zinc-300">
              Non è possibile prenotare online con questo numero. Contatta direttamente il salone
              su WhatsApp.
            </p>
          </div>
        )}

        {waitlist && (
          <div
            data-testid="waitlist-summary"
            className="space-y-2 rounded-2xl border border-amber-400/30 bg-amber-400/10 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-amber-300">
              Sei in lista d&apos;attesa
            </p>
            <p className="text-sm text-zinc-200">
              {waitlist.nome} — {waitlist.servizio}, {waitlist.giorno} alle {waitlist.ora}
              {waitlist.barbiere ? ` · con ${waitlist.barbiere}` : ""}
            </p>
            <p className="text-xs leading-relaxed text-zinc-400">
              Lo slot è occupato: ti avvisiamo su WhatsApp appena si libera un posto.
            </p>
            {waitlist.alternative && (
              <p
                data-testid="waitlist-alternatives"
                className="text-xs leading-relaxed text-amber-200"
              >
                Orari liberi quel giorno: {waitlist.alternative} — scrivimi se preferisci uno di
                questi e sposto subito la prenotazione.
              </p>
            )}
          </div>
        )}

        {booking && (
          <div
            data-testid="booking-summary"
            className="space-y-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4"
          >
            <p className="text-xs font-bold uppercase tracking-widest text-emerald-400">
              Prenotazione confermata
            </p>
            <p className="text-sm text-zinc-200">
              {booking.nome} — {booking.servizio}, {booking.giorno} alle {booking.ora}
              {booking.barbiere && (
                <span className="block text-xs text-zinc-400">Con: {booking.barbiere}</span>
              )}
            </p>
            <p className="text-xs text-zinc-400">
              Il tuo appuntamento è già in agenda. Ti aspettiamo in salone!
            </p>
            <a
              data-testid="btn-send-whatsapp"
              href={waText}
              onClick={openExternal(waText, waIntent(WHATSAPP_DIGITS, waTextBody))}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-xl border border-white/15 px-4 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white/5"
            >
              <FaWhatsapp size={16} className="text-[#25D366]" /> Invia riepilogo su WhatsApp
            </a>
            <a
              data-testid="btn-pay-advance"
              href={LINKS.pay}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 rounded-xl bg-amber-400 px-4 py-3 text-sm font-extrabold text-zinc-950 shadow-[0_0_20px_rgba(251,191,36,0.25)] transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-[0.98]"
            >
              <CreditCard size={16} />
              {priceFor(services, booking.servizio) !== null
                ? `Pagare — ${priceFor(services, booking.servizio)}€`
                : "Pagare"}
            </a>
            {priceFor(services, booking.servizio) !== null && (
              <p data-testid="pay-amount-hint" className="text-center text-[11px] text-zinc-500">
                Nella pagina SumUp scrivi l&apos;importo:{" "}
                <span className="font-bold text-amber-300">
                  {priceFor(services, booking.servizio)}€
                </span>
              </p>
            )}
          </div>
        )}
      </div>

      {!booking && !waitlist && (
        <div className="flex gap-2 overflow-x-auto px-5 pb-2" data-testid="chat-quick">
          {QUICK.map((c, i) => (
            <button
              key={c.label}
              data-testid={`chat-quick-${i}`}
              onClick={() => send(c.msg)}
              className={`shrink-0 rounded-full border px-4 py-1.5 text-xs font-bold transition-colors duration-200 ${
                c.label.startsWith("Con ")
                  ? "border-blue-500/30 bg-blue-500/10 text-blue-300 hover:bg-blue-500/20"
                  : "border-amber-400/30 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      )}

      <form
        className="flex items-center gap-2 border-t border-white/10 px-4 py-3 pb-6"
        onSubmit={(e) => {
          e.preventDefault();
          send(input);
        }}
      >
        <input
          data-testid="chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Scrivi qui…"
          className="h-11 flex-1 rounded-full border border-white/10 bg-zinc-900 px-4 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50"
        />
        <button
          data-testid="chat-send"
          type="submit"
          disabled={loading}
          className="flex h-11 w-11 items-center justify-center rounded-full bg-amber-400 text-zinc-950 transition-[background-color,transform] duration-200 hover:bg-amber-300 active:scale-95 disabled:opacity-50"
        >
          <Send size={17} />
        </button>
      </form>
    </motion.div>
  );
}
